import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { FieldValueFlatForm } from 'components/dataCreationForms/withFieldValueForm';
import { FieldTranslationKeys } from 'intl/keys';
import { TranslationKey } from 'intl';
import { useInputForm } from 'components/dataCreationForms/withInputForm';
import { FormFieldProps } from 'components/inspection/Form/types';
import {
  allAvailableLocaleCodes,
  getAvailableAppLocales,
} from 'intl/mappings';
import {
  CreateFieldValueOnView,
  FieldValueOnView,
  FieldValueType,
} from 'presentation/fieldValue/fieldValueOnView';
import { useIntl } from 'react-intl';
import { Identificable, LabelledEntity } from 'shared/types/commonView';
import { AVAILABLE_PROCESSES } from 'shared/domain/process/types/model';
import {
  filterLanugagesToChoose,
  findLastIndex,
  getLabelChangeDisabledReason,
  getLanguageSelectDisabledReason,
} from './model';
import { FieldValueFormPresentational } from './presentational';
import { LanguageOption } from './types';
import { CommonTranslationKeys } from 'intl/keys';

function defaultFilter<T>(x: T): boolean {
  return Boolean(x);
}

function createLanguageSelectField(
  index: number,
  filterFunction: (e: LabelledEntity) => boolean = defaultFilter,
  disabled?: string
): FormFieldProps<Record<string, LabelledEntity>> {
  const available = getAvailableAppLocales().filter(filterFunction);
  return {
    formKey: `valueTranslations${index}`,
    labelId: FieldTranslationKeys.valuesLanguage,
    required: true,
    fieldName: `field-value-select-language-${index}`,
    available: available,
    getOptionLabel: (option: { label: string }) => option.label,
    getOptionSelected: (
      option: LabelledEntity,
      value: LabelledEntity
    ): boolean => option._id === value._id,
    minRows: 3,
    disabled: disabled ? { reason: disabled } : undefined,
    reserveSpaceForHelperText: false,
    dense: false,
    'data-qa': 'field_value_language_field',
    issueParams: null,
  };
}

function createLabelField(
  index: number,
  disabled?: string
): FormFieldProps<Record<string, string>> {
  return {
    formKey: `label${index}`,
    required: true,
    labelId: FieldTranslationKeys.valuesLabel,
    fieldName: `field-value-label-${index}`,
    disabled: disabled ? { reason: disabled } : undefined,
    reserveSpaceForHelperText: false,
    dense: false,
    'data-qa': 'field_value_label_field',
    issueParams: null,
  };
}

function createInitialLanguageOptions(values: {
  _field?: CreateFieldValueOnView | FieldValueOnView;
}): LanguageOption[] {
  if (!values._field) {
    return [
      {
        languageSelect: createLanguageSelectField(0),
        labelField: createLabelField(0),
        removed: false,
        key: `${Date.now()},0`,
        isRemote: false,
      },
    ];
  }
  const filterValues: string[] = [];
  return values._field.valueTranslations.map(
    (
      element: Identificable & { type: FieldValueType },
      index: number,
      translations: (Identificable & { type: FieldValueType })[]
    ) => {
      const isRemote = element.type === FieldValueType.fromRemote;
      const disabledLanguageSelect = getLanguageSelectDisabledReason({
        isHustroDefault: !values._field!.canEditTranslations,
        isRemote: isRemote,
        hasNext: index < translations.length - 1,
      });
      const disabledLabelChange = getLabelChangeDisabledReason({
        isHustroDefault: !values._field!.canEditTranslations,
      });
      const result = {
        languageSelect: createLanguageSelectField(
          index,
          filterLanugagesToChoose([...filterValues]),
          disabledLanguageSelect
        ),
        labelField: createLabelField(index, disabledLabelChange),
        removed: false,
        key: `${Date.now()},${index}`,
        isRemote,
      };
      filterValues.push(element._id);
      return result;
    }
  );
}

/** Positions of the languages the user removed: validation skips them. */
function findRemovedLanguageIndexes(languageOptions: LanguageOption[]): number[] {
  return languageOptions.flatMap((option, index) => (option.removed ? [index] : []));
}

export function FieldValueForm(props: {
  onSubmit: () => void;
  onCancel: () => void;
  isPosting: boolean;
}): ReactElement {
  const intl = useIntl();
  const { onSubmit, onCancel, isPosting } = props;
  const { setValues, values } = useInputForm();

  const [languageOptions, setLanguageOptions] = useState(
    createInitialLanguageOptions(values as FieldValueFlatForm)
  );


  useEffect(() => {
    if (!values._field) {
      setValues(`valueTranslations${0}`, undefined, {
        skipFormEdit: true,
        skipValidation: true,
        forceKeySet: true,
      });
      setValues(`label${0}`, undefined, {
        skipFormEdit: true,
        skipValidation: true,
        forceKeySet: true,
      });
    }
    // onmount once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addLanguageOption = useCallback(() => {
    setLanguageOptions((prev) => {
      const index = prev.length;
      const filterValues: string[] = [];
      const newPrev = prev.map((option) => {
        option.languageSelect.disabled = {
          reason: CommonTranslationKeys.disabledCannotChangeLanguageAfterAddNext,
        };
        if (!option.removed) {
          filterValues.push(values[option.languageSelect.formKey]._id);
        }
        return option;
      });
      setValues(`valueTranslations${index}`, undefined, {
        skipFormEdit: true,
        skipValidation: true,
        forceKeySet: true,
      });
      setValues(`label${index}`, undefined, {
        skipFormEdit: true,
        skipValidation: true,
        forceKeySet: true,
      });
      return [
        ...newPrev,
        {
          languageSelect: createLanguageSelectField(
            index,
            filterLanugagesToChoose([...filterValues])
          ),
          labelField: createLabelField(index),
          removed: false,
          key: `${Date.now()},${index}`,
          isRemote: false,
        },
      ];
    });
  }, [values, setValues]);

  const removeLanguageOption = useCallback(
    (index: number) => {
      const removedOption = languageOptions.find((_, optionIndex) => optionIndex === index);
      if (!removedOption || removedOption.isRemote) {
        return;
      }
      const nextLanguageOptions = languageOptions.map((option, optionIndex) =>
        optionIndex === index ? { ...option, removed: true } : option
      );
      setLanguageOptions(nextLanguageOptions);
      // Validation skips the removed rows.
      setValues('ignoreIndexes', findRemovedLanguageIndexes(nextLanguageOptions), {
        skipFormEdit: true,
        skipValidation: true,
      });
    },
    [languageOptions, setValues]
  );

  const processesCheckboxes: FormFieldProps<{
    processes: string[];
  }> = useMemo(
    () => ({
      formKey: 'processes',
      labelId: FieldTranslationKeys.valueProcessesDescription,
      fieldName: `field-value-processes`,
      available: AVAILABLE_PROCESSES.map((process) => {
        return {
          _id: process._id,
          label: process.label,
        };
      }),
      required: false,
      getOptionLabel: (option: { label: string }) => option.label,
      reserveSpaceForHelperText: false,
      dense: false,
      'data-qa': 'field_value_processes_field',
      issueParams: null,
    }),
    [intl]
  );

  const disableAddLanguageOption = useMemo((): TranslationKey | undefined => {
    if (values._field && !values._field.canEditTranslations) {
      return CommonTranslationKeys.disabledCannotChangeHustroDefault;
    }
    const activeLanguageOptions = languageOptions.filter((option) => {
      return !option.removed;
    });
    if (activeLanguageOptions.length === allAvailableLocaleCodes.length) {
      return CommonTranslationKeys.disabledCannotAddMoreTranslations;
    }

    const lastNonRemovedIndex = findLastIndex((option) => {
      return !option.removed;
    }, languageOptions);
    const lastNonRemoved = languageOptions[lastNonRemovedIndex];
    if (
      !values[lastNonRemoved.labelField.formKey] ||
      !values[lastNonRemoved.languageSelect.formKey]
    ) {
      return CommonTranslationKeys.disabledCannotAddTranslationMustBeFilled;
    }
    return undefined;
  }, [languageOptions, values]);

  const disableRemoveLanguageOption = useMemo(() => {
    if (values._field) {
      return !values._field.canEditTranslations;
    }
    return false;
  }, [values._field]);

  return (
    <FieldValueFormPresentational
      onSubmit={onSubmit}
      onCancel={onCancel}
      isPosting={isPosting}
      languageOptions={languageOptions}
      addLanguageOption={addLanguageOption}
      removeLanguageOption={removeLanguageOption}
      processesCheckboxes={processesCheckboxes}
      disableAddLanguageOption={disableAddLanguageOption}
      disableRemoveLanguageOption={disableRemoveLanguageOption}
    />
  );
}
